// Vercel Deployment Trigger & Clean Commit: 2024-05-16T06:35:00.000Z (example)
// EXPLICIT VERCEL DEPLOY TRIGGER: ${new Date().toISOString()}
// FORCE DEPLOYMENT TIMESTAMP: ${new Date().toISOString()} - URGENT DEPLOY
// Vercel Rebuild Trigger - Ensuring latest changes are deployed
// src/pages/DeliveryDetails.tsx
// Vercel Rebuild Attempt - V5 - Forcing Env Var Pickup

// Trigger rebuild based on 7c7c915 state (v3)
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/components/ui/use-toast";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useLoadScript } from "@react-google-maps/api";
import { PlaceAutocomplete } from "@/components/ui/PlaceAutocomplete";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react"; // For accordion toggle
import { useScreenSize } from "@/hooks/use-screen-size";
import { loadStripe } from '@stripe/stripe-js'; // Import loadStripe
import { isStoreOpen } from "@/utils/businessHours"; // Import business hours utility
import SMSOptInSection from "@/components/checkout/SMSOptInSection";

// Initialize Stripe.js outside of the component rendering logic
// Ensure VITE_STRIPE_PUBLISHABLE_KEY is set in your .env file
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);

// Define libraries array for Google Maps - Removed "localContext"
const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ["places", "geometry"]; // Keep places and geometry if needed

// Helper function to sort days of the week
const sortDaysOfWeek = (days: string[]) => {
  const dayOrder = {
    'Monday': 0,
    'Tuesday': 1,
    'Wednesday': 2,
    'Thursday': 3,
    'Friday': 4,
    'Saturday': 5,
    'Sunday': 6
  };
  
  return [...days].sort((a, b) => dayOrder[a as keyof typeof dayOrder] - dayOrder[b as keyof typeof dayOrder]);
};

// Email validation function
const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  email = email.trim();
  if (email === '') return false;
  
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

// Function to check if we're past the cutoff time for same-day delivery
const isPastCutoffTime = (cutoffTime: string, floristTimezone: string = 'Australia/Sydney'): boolean => {
  if (!cutoffTime) return true; // If no cutoff time, assume we're past it
  
  try {
    // Parse the cutoff time (e.g. "14:00" to hours and minutes)
    const [hours, minutes] = cutoffTime.split(':').map(Number);
    
    // Get current time in florist's timezone
    const now = new Date();
    const floristTime = new Intl.DateTimeFormat('en-US', {
      timeZone: floristTimezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    }).format(now);
    
    const [currentHours, currentMinutes] = floristTime.split(':').map(Number);
    
    // Compare times
    if (currentHours > hours) return true;
    if (currentHours === hours && currentMinutes >= minutes) return true;
    return false;
  } catch (error) {
    console.error('Error parsing cutoff time:', error);
    return true; // If there's an error, assume we're past cutoff to be safe
  }
};

// Distance calculation function using Haversine formula
const calculateDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const distance = R * c; // Distance in km
  return distance;
};

function deg2rad(deg: number): number {
  return deg * (Math.PI/180);
}

const DeliveryDetails = () => {
  const navigate = useNavigate();
  const { orderId: routeOrderId } = useParams(); // Renamed to avoid conflict with createdOrderId
  const { items, total, clear } = useCart();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [floristData, setFloristData] = useState<any>(null);
  const [businessHours, setBusinessHours] = useState<any>(null);
  
  // Delivery method - pickup or delivery
  const [method, setMethod] = useState<"pickup" | "delivery">("delivery");
  
  // Delivery options
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [address, setAddress] = useState({
    street: "",
    suburb: "",
    state: "NSW",
    postcode: ""
  });

  const [addressCoordinates, setAddressCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [distanceError, setDistanceError] = useState<string | null>(null);

  // Ref for the uncontrolled address input
  const autocompleteInputRef = useRef<HTMLInputElement>(null);

  // Buyer and recipient information
  const [buyerInfo, setBuyerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });

  const [recipientInfo, setRecipientInfo] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  // SMS opt-in state
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [smsUpdatesFee, setSmsUpdatesFee] = useState(0);

  // Load Google Maps script
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Add state for general payment status
  const [paymentStatus, setPaymentStatus] = useState('INITIAL'); // Keep for general payment status

  // Refs for mounting payment elements
  const cardElementRef = useRef<HTMLDivElement>(null);

  // --- Re-add validationErrors state --- 
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    distance?: string;
    date?: string;
    address?: string;
  } | null>(null);

  // Add state for store location
  const [storeLocation, setStoreLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Add state for mobile order summary
  const [isMobileOrderSummaryExpanded, setIsMobileOrderSummaryExpanded] = useState(false);
  const [isMobileFloristDetailsExpanded, setIsMobileFloristDetailsExpanded] = useState(false);

  // Add state to control the calendar's displayed month to fix rendering issues
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Load existing order if an order ID is provided
  useEffect(() => {
    const loadOrder = async () => {
      if (!routeOrderId) return; // Use renamed routeOrderId
      
      try {
        // Check if this is a temporary ID
        if (routeOrderId.startsWith('temp_')) { // Use renamed routeOrderId
          console.log('Using temporary order data from session storage');
          // Get data from session storage
          const email = sessionStorage.getItem('checkout_email');
          const storeId = sessionStorage.getItem('checkout_store_id');
          
          if (email) {
            setBuyerInfo(prev => ({ 
              ...prev, 
              email
            }));
          }
          
          if (storeId) {
            setStoreLocation({
              lat: 0,
              lng: 0
            });
          }
          
          // No need to load from database for temp orders
          return;
        }
        
        // For real orders, load from database
        const { data: orderResult, error: orderError } = await supabase
          .from('orders')
          .select('customer_email, florist_id, store_id')
          .eq('id', routeOrderId as any) // Use renamed routeOrderId
          .single();
        
        if (orderError) {
          console.error('Error fetching order details:', orderError);
          return;
        }
        
        if (orderResult) {
          const typedOrderResult = orderResult as { customer_email?: string | null; florist_id?: string | null; store_id?: string | null };

          if (typedOrderResult.customer_email) {
            setBuyerInfo(prev => ({ 
              ...prev, 
              email: typedOrderResult.customer_email! 
            }));
          }
          
          const storeIdForProfile = typedOrderResult.store_id || typedOrderResult.florist_id;
          if (storeIdForProfile) {
            // Load store details
            const { data: storeProfileResult, error: storeProfileError } = await supabase
              .from('store_profiles')
              .select('*')
              .eq('id', storeIdForProfile as any)
              .single();
              
            if (storeProfileError) {
              console.error("[DeliveryDetails] Error fetching store profile:", storeProfileError);
            } else if (storeProfileResult) {
              const typedStoreProfileResult = storeProfileResult as any; // Using 'any' for broader compatibility
              setFloristData(typedStoreProfileResult);
              if (typedStoreProfileResult.latitude && typedStoreProfileResult.longitude) {
                setStoreLocation({
                  lat: typedStoreProfileResult.latitude,
                  lng: typedStoreProfileResult.longitude
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
      }
    };
    
    loadOrder();
  }, [routeOrderId]);

  // Fetch user session
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        console.log("[DeliveryDetails] Authenticated user found, fetching profile for ID:", user.id);
        
        // Rely on RLS to filter for the current user. 
        // The .single() will ensure only one row is returned, which is expected if user_id is UNIQUE.
        const { data: profile, error: profileErrorAltered } = await supabase
          .from('customer_profiles')
          .select('full_name, email, suburb, state, postcode, unit_number, street_number, street_name, phone')
          .eq('id', user.id as any)
          .single();
          
        if (profileErrorAltered) {
            console.error("[DeliveryDetails] Error fetching user profile:", profileErrorAltered);
            toast({ title: "Profile Error", description: "Could not load your profile details.", variant: "destructive" });
            setBuyerInfo(prev => ({
              ...prev,
              email: user.email || '' // Fallback to auth user email
            }));
            return; 
        }
          
        if (profile) {
          console.log("[DeliveryDetails] User profile data found:", profile);
          const typedProfile = profile as { full_name?: string | null; email?: string | null; suburb?: string | null; state?: string | null; postcode?: string | null; unit_number?: string | null; street_number?: string | null; street_name?: string | null; phone?: string | null };
          // Attempt to split full_name, otherwise use full_name as firstName
          const nameParts = typedProfile.full_name?.split(' ') || [];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          setBuyerInfo({
            firstName: firstName,
            lastName: lastName,
            email: typedProfile.email || user.email || '', // Prefer auth user email as primary source
            phone: typedProfile.phone || '' // Phone is not in profiles schema, initialize as empty
          });

          setAddress({
            street: `${typedProfile.unit_number || ''} ${typedProfile.street_number || ''} ${typedProfile.street_name || ''}`.trim(),
            suburb: typedProfile.suburb || '',
            state: typedProfile.state || 'NSW',
            postcode: typedProfile.postcode || ''
          });
        } else {
          console.warn("[DeliveryDetails] User authenticated, but no profile record found for ID:", user.id);
          setBuyerInfo(prev => ({
            ...prev,
            email: user.email || ''
          }));
        }
      } else {
        console.log("[DeliveryDetails] No authenticated user found.");
      }
    };
    getUser();
  }, []);

  // Fetch florist data
  useEffect(() => {
    const fetchFloristData = async () => {
      if (!items || items.length === 0) {
        navigate('/cart');
        return;
      }

      try {
        // Use only floristId as storeId is likely not on CartItem type
        const floristId = items[0]?.floristId; 
        if (!floristId) {
          throw new Error("No florist information found");
        }

        const { data, error } = await supabase
          .from('store_profiles')
          .select('*')
          .eq('id', floristId as any)
          .single();

        if (error) throw error;
        if (!data) throw new Error("Store not found");
        
        const typedData = data as any; // Using 'any' for broader compatibility

        setFloristData(typedData);
        setBusinessHours(typedData.business_hours);
        
        // Set store location if available
        if (typedData.latitude && typedData.longitude) {
          setStoreLocation({
            lat: typedData.latitude,
            lng: typedData.longitude
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching florist data:", error);
        toast({
          title: "Error",
          description: "Failed to load florist information",
          variant: "destructive"
        });
        navigate('/cart');
      }
    };

    fetchFloristData();
  }, [items, navigate, toast]);

  // Handle place selection
  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (!place || !place.address_components) return;
    // Clear any previous address or distance validation errors
    setValidationErrors(prev => ({ ...prev, address: undefined, distance: undefined }));

    let streetNumber = '';
    let subpremise = '';
    let route = '';
    let suburb = '';
    let state = '';
    let postcode = '';

    place.address_components.forEach(component => {
      const types = component.types;
      if (types.includes('subpremise')) {
        subpremise = component.long_name;
      } else if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('route')) {
        route = component.long_name;
      } else if (types.includes('locality') || types.includes('sublocality')) {
        suburb = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      } else if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
    });

    // Construct street address, prioritizing subpremise
    let constructedStreet = '';
    if (subpremise) constructedStreet += `${subpremise}/`;
    if (streetNumber) constructedStreet += streetNumber;
    if (route) constructedStreet += (constructedStreet ? ' ' : '') + route;

    // Use constructed street if available, otherwise fallback to place.name or empty string
    const street = constructedStreet || place.name || '';
    
    // Update the address state (for preview box etc.)
    setAddress({
      street,
      suburb: suburb || '',
      state: state || 'NSW',
      postcode: postcode || ''
    });

    // Manually set the input's value via the ref AFTER selection and parsing
    const displayAddress = `${street}, ${suburb}, ${state} ${postcode}`.trim().replace(/, $/, ''); // Clean up trailing comma
    if (autocompleteInputRef.current) {
      autocompleteInputRef.current.value = displayAddress;
    }

    if (place.geometry?.location) {
      const coordinates = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      
      setAddressCoordinates(coordinates);
      
      // Validate distance if we have store location
      if (storeLocation && method === 'delivery') {
        const distance = calculateDistance(
          coordinates.lat, 
          coordinates.lng, 
          storeLocation.lat, 
          storeLocation.lng
        );
        
        const deliveryRadius = floristData?.delivery_radius || 0;
        if (distance > deliveryRadius) {
          setValidationErrors(prev => ({
            ...prev,
            distance: `This address is ${distance.toFixed(1)}km away, which exceeds the ${deliveryRadius}km delivery radius.`
          }));
        }
      }
    }
  };

  // Validate form fields before continuing
  const validateForm = () => {
    let isValid = true;
    const errors: any = {};
    
    // Validate delivery date
    if (!deliveryDate) {
      errors.date = 'Please select a delivery date';
      isValid = false;
    }
    
    // Validate email
    if (!validateEmail(buyerInfo.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Validate address for delivery
    if (method === 'delivery') {
      if (!address.street || !address.suburb) {
        errors.address = 'Please enter a complete delivery address';
        isValid = false;
      }
      
      // Check for distance validation error
      if (validationErrors?.distance) {
        errors.distance = validationErrors.distance;
        isValid = false;
      }
    }
    
    setValidationErrors(errors);
    return isValid;
  };

  // Rename handleContinue and add payment logic
  const handlePlaceOrder = async () => {
    const isFormValid = validateForm();
    if (!isFormValid) {
      toast({ title: "Validation Error", description: "Please check your details and try again.", variant: "destructive" });
      return;
    }

    setPaymentStatus('PROCESSING');
    setIsLoading(true);
    setError(null);

    try {
      // 1. Create the order record via Edge Function (existing logic)
      const orderPayloadForRecord = {
          amount: calculateTotalInCents() / 100, // Pass dollars
          deliveryFee: (method === 'delivery' ? (floristData?.delivery_fee || 0) : 0),
          storeId: floristData?.id,
          customerEmail: buyerInfo.email,
          orderItems: items.map(item => ({ // Map cart items structure
              name: item.title,
              quantity: item.quantity,
              price: item.price,
              size: item.size || null,
              // Include image fields so florist order details can render thumbnails
              main_image_url: item.image || null,
              image_url: item.image || null,
              product_id: item.productId || null 
          })),
          userId: user?.id, 
          deliveryAddress: method === 'delivery' ? JSON.stringify(address) : null,
          deliveryDate: deliveryDate ? format(deliveryDate, 'yyyy-MM-dd') : null,
          deliveryTime: null, 
          deliveryType: method,
          isDelivery: method === 'delivery',
          isSameDay: deliveryDate ? format(deliveryDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') : false,
          specialInstructions: specialInstructions,
          buyerFirstName: buyerInfo.firstName,
          buyerLastName: buyerInfo.lastName,
          buyerPhone: buyerInfo.phone,
          recipientFirstName: recipientInfo.firstName,
          recipientLastName: recipientInfo.lastName,
          recipientPhone: recipientInfo.phone,
          giftMessage: specialInstructions, 
          deliveryCoordinates: addressCoordinates,
          // SMS opt-in data
          smsOptIn: smsOptIn,
          smsUpdatesFee: smsUpdatesFee,
        };

        console.log('Calling create-order-record Edge Function with payload:', orderPayloadForRecord);
        const { data: createOrderResponse, error: createOrderError } = await supabase.functions.invoke(
          'create-order-record', 
          { body: orderPayloadForRecord }
        );

        if (createOrderError || !createOrderResponse?.success || !createOrderResponse?.orderId) {
          console.error('Error calling create-order-record function:', createOrderError, createOrderResponse);
          throw new Error(createOrderResponse?.error || createOrderError?.message || 'Failed to create order record before payment.');
        }

        const createdOrderId = createOrderResponse.orderId;
        console.log('Order record created with ID (internal_order_id for Stripe):', createdOrderId);

      // 2. Prepare payload for Stripe Checkout Session creation
      const itemsSubtotalInCents = Math.round((total || 0) * 100);
      const deliveryFeeInCents = method === 'delivery' ? Math.round((floristData?.delivery_fee || 0) * 100) : 0;
      const smsFeesInCents = Math.round(smsUpdatesFee * 100);
      const grandTotalInCents = itemsSubtotalInCents + deliveryFeeInCents + smsFeesInCents;

      console.log('Items subtotal in cents for commission:', itemsSubtotalInCents);
      console.log('Delivery fee in cents:', deliveryFeeInCents);
      console.log('SMS fees in cents:', smsFeesInCents);
      console.log('Grand total in cents for Stripe payload:', grandTotalInCents);

      const stripePaymentPayload = {
        order_items: items.map(item => ({ // Renamed from orderItems for consistency if function expects this
          name: item.title,
          amount: Math.round(item.price * 100), 
          currency: 'aud', 
          quantity: item.quantity,
          description: item.size || undefined, 
        })),
        delivery_fee_item: deliveryFeeInCents > 0 ? { // New key, matches expected object structure for a line item part
          name: 'Delivery Fee',
          amount: deliveryFeeInCents,
          currency: 'aud',
          quantity: 1,
        } : undefined,
        sms_fee_item: smsFeesInCents > 0 ? {
          name: 'SMS Updates Service',
          amount: smsFeesInCents,
          currency: 'aud',
          quantity: 1,
        } : undefined,
        subtotal_for_commission_cents: itemsSubtotalInCents, // New key
        grand_total_cents: grandTotalInCents, // New key
        currency: 'aud', 
        store_id: floristData?.id,
        customer_email: buyerInfo.email, // Keep consistent or change to customer_email if preferred
        internal_order_id: createdOrderId, 
      };
      console.log('Payload for create-stripe-payment (with new keys):', stripePaymentPayload);

      console.log('Calling create-stripe-payment Edge Function with payload:', stripePaymentPayload);
      const { data: stripeSessionResponse, error: stripeSessionError } = await supabase.functions.invoke(
        'create-stripe-payment',
        { body: stripePaymentPayload }
          );

      if (stripeSessionError) {
        console.error('Error invoking create-stripe-payment function:', stripeSessionError);
        throw new Error(stripeSessionError.message || 'Could not initiate Stripe payment session.');
      }

      if (!stripeSessionResponse?.sessionId) {
        console.error('Error from create-stripe-payment function - missing sessionId:', stripeSessionResponse);
        throw new Error(stripeSessionResponse?.error || 'Failed to get Stripe session ID.');
      }

      console.log('Stripe Checkout Session created:', stripeSessionResponse.sessionId);
      setPaymentStatus('REDIRECTING_TO_STRIPE'); // New status

      // 3. Redirect to Stripe Checkout
      const stripe = await stripePromise;
      if (stripe) {
        const { error: redirectToCheckoutError } = await stripe.redirectToCheckout({
          sessionId: stripeSessionResponse.sessionId,
        });
        if (redirectToCheckoutError) {
          console.error('Error redirecting to Stripe Checkout:', redirectToCheckoutError);
          // This error usually means client-side issues (e.g. invalid publishable key, network)
          // or if Stripe itself can't process the redirect. The user might still be on your page.
          throw new Error(redirectToCheckoutError.message || 'Could not redirect to Stripe.');
        }
        // If redirectToCheckout is successful, the user is navigated away, 
        // so code below this point might not execute immediately unless there was an issue preventing redirect.
      } else {
        throw new Error('Stripe.js not loaded, cannot redirect to checkout.');
          }

      // Clear cart and navigate on successful *initiation* of redirect. 
      // Actual payment success handled by Stripe redirecting to your success_url.
      // It might be better to clear cart and navigate *only* on the success_url page.
      // For now, let's assume if redirectToCheckout doesn't throw, it's moving forward.
      // toast({ title: "Success", description: "Redirecting to Stripe for payment..." });
      // clear(); // Consider moving clear() to the order confirmation page after successful payment
      // navigate(`/order-confirmation?orderId=${createdOrderId}`); // User will be redirected by Stripe

      } catch (error: any) {
        console.error('Error processing order/payment:', error);
        setError(error.message || 'Failed to process your order. Please check details and try again.');
        setPaymentStatus('ERROR');
      toast({ title: "Payment Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Calculate the amount in cents
  const calculateTotalInCents = () => {
    const totalAmount = (total || 0) + (method === 'delivery' ? (floristData?.delivery_fee || 0) : 0) + smsUpdatesFee;
    return Math.round(totalAmount * 100); // Convert to cents
  };

  // Add a general cleanup for cardElement on component unmount
  useEffect(() => {
    // This is a general cleanup for the cardElement when the component unmounts
    return () => {
      // if (cardElement) { // cardElement removed
      //   console.log('Unmounting DeliveryDetails: Destroying cardElement from state.');
      //   cardElement.destroy().catch((err: any) => console.error('Error destroying cardElement from state on component unmount:', err));
      // }
    };
  // }, [cardElement]); // cardElement removed from dependencies
  }, []); // Empty dependency array if no specific cleanup needed for Stripe.js redirect flow

  const screenSize = useScreenSize();
  const isMobile = screenSize.equals('xs') || screenSize.equals('sm');

  // Add function to copy buyer info to recipient info
  const copyBuyerToRecipient = () => {
    setRecipientInfo({
      firstName: buyerInfo.firstName,
      lastName: buyerInfo.lastName,
      phone: buyerInfo.phone,
    });
  };

  return (
    <div className="min-h-screen bg-brand-accent pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Progress Steps */}
        <div className="mb-8 max-w-2xl mx-auto">
          <ol className="flex items-center w-full">
            <li className="flex w-full items-center text-gray-500 after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-300 after:border-1 after:inline-block">
              <span className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full shrink-0 text-xs">
                1
              </span>
            </li>
            <li className="flex w-full items-center text-white after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-300 after:border-1 after:inline-block">
              <span className="flex items-center justify-center w-8 h-8 bg-[#967F81] rounded-full shrink-0 text-xs">
                2
              </span>
            </li>
            <li className="flex items-center">
              <span className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full shrink-0 text-xs">
                3
              </span>
            </li>
          </ol>
              </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          
          {/* Mobile Order Summary - Top of page, collapsible */}
          <div className="lg:hidden col-span-1">
            <Card className="shadow-md mb-6">
              <CardHeader 
                className="pb-3 cursor-pointer" 
                onClick={() => setIsMobileOrderSummaryExpanded(!isMobileOrderSummaryExpanded)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Order Summary</CardTitle>
                  {isMobileOrderSummaryExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
              </CardHeader>
              {isMobileOrderSummaryExpanded && (
                <CardContent className="pt-0">
                  {items && items.map(item => (
                    <div key={item.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
            <div className="flex items-center">
                        <img src={item.image} alt={item.title} className="w-10 h-10 rounded object-cover mr-2" />
                        <div>
                          <p className="font-medium text-xs">{item.title}</p>
                          <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
              </div>
            </div>
                      <p className="text-xs font-medium">${(item.price * item.quantity).toFixed(2)}</p>
              </div>
                  ))}
                  <Separator className="my-3" />
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Subtotal</span>
                      <span>${total?.toFixed(2)}</span>
            </div>
                    <div className="flex justify-between text-xs">
                      <span>Delivery Fee</span>
                      <span>
                        {method === 'delivery' ? `$${(floristData?.delivery_fee || 0).toFixed(2)}` : 'N/A'}
                      </span>
          </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold text-sm">
                      <span>Total</span>
                      <span>${((total || 0) + (method === 'delivery' ? (floristData?.delivery_fee || 0) : 0)).toFixed(2)}</span>
        </div>
                  </div>

                  {/* Mobile Florist Details - Collapsible section */}
                  {floristData && (
                    <div className="mt-4 pt-4 border-t">
                      <div 
                        className="flex items-center justify-between cursor-pointer mb-2"
                        onClick={() => setIsMobileFloristDetailsExpanded(!isMobileFloristDetailsExpanded)}
            >
                        <p className="font-medium text-sm">{method === 'pickup' ? 'Business Hours' : 'Florist Details'}</p>
                        {isMobileFloristDetailsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                      
                      {isMobileFloristDetailsExpanded && (
                        <div className="space-y-3 text-xs">
                          {/* Store Info */}
                      <div>
                            <p className="font-medium">{floristData.store_name}</p>
                            <div className="text-gray-600 mt-1">
                              {floristData.unit_number && `${floristData.unit_number}/`}
                              {floristData.street_number} {floristData.street_name}
                              <br />
                              {floristData.suburb}, {floristData.state} {floristData.postcode}
                      </div>
                    </div>
                          
                          {method === 'pickup' ? (
                            /* Business Hours for Pickup */
                            <div>
                              <p className="font-medium mb-1">Store Hours</p>
                              <div className="bg-blue-50 p-2 rounded space-y-1">
                                {floristData.business_hours && typeof floristData.business_hours === 'object' ? (
                                  Object.entries(floristData.business_hours).map(([day, hours]: [string, any]) => (
                                    <div key={day} className="flex justify-between">
                                      <span className="text-gray-700 capitalize">{day}</span>
                                      <span className="text-gray-600">
                                        {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                                      </span>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-gray-600">Business hours not available</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            /* Delivery Information */
                            <>
                              {/* Same Day Delivery */}
                              {floristData.same_day_enabled && floristData.delivery_cutoff_times && (
                                <div>
                                  <p className="font-medium mb-1 text-brand-primary">Same Day Delivery</p>
                                  <div className="bg-[#967F81]/10 p-2 rounded space-y-1">
                                    {Object.entries(floristData.delivery_cutoff_times).map(([day, cutoffTime]) => (
                                      <div key={day} className="flex justify-between">
                                        <span className="text-gray-700">{day}</span>
                                        <span className="text-gray-600">Before {cutoffTime as string}</span>
                  </div>
                ))}
                  </div>
                    </div>
                  )}
                              
                              {/* Delivery Info */}
                              <div>
                                <p className="font-medium mb-1">Delivery Info</p>
                                <div className="space-y-1">
                                  <div className="flex justify-between">
                                    <span>Radius</span>
                                    <span>{floristData.delivery_radius || 0}km</span>
                  </div>
                                  <div className="flex justify-between">
                                    <span>Fee</span>
                                    <span>${(floristData.delivery_fee || 0).toFixed(2)}</span>
                </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </CardContent>
            )}
              {/* Compact summary when collapsed */}
            {!isMobileOrderSummaryExpanded && (
                <CardContent className="pt-0 pb-3">
                  <div className="flex justify-between items-center text-sm">
                    <span>{items?.length} item(s)</span>
                    <span className="font-bold">${((total || 0) + (method === 'delivery' ? (floristData?.delivery_fee || 0) : 0) + smsUpdatesFee).toFixed(2)}</span>
                  </div>
                </CardContent>
            )}
          </Card>
        </div>

          {/* Left Column: Delivery & Payment Details */}
          <div className="lg:col-span-3 space-y-5">
            
            {/* Delivery Method Card */}
            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Delivery Method</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-4">
                  <Button
                    variant={method === 'delivery' ? 'default' : 'outline'}
                          onClick={() => setMethod('delivery')}
                    className={`flex-1 h-9 text-sm ${method === 'delivery' ? 'bg-[#967F81] text-white' : ''}`}
                        >
                    Delivery
                  </Button>
                  <Button
                    variant={method === 'pickup' ? 'default' : 'outline'}
                          onClick={() => setMethod('pickup')}
                    className={`flex-1 h-9 text-sm ${method === 'pickup' ? 'bg-[#967F81] text-white' : ''}`}
                        >
                    Pickup
                  </Button>
                      </div>
              </CardContent>
            </Card>

            {/* Delivery / Pickup Details Card */}
            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{method === 'delivery' ? 'Delivery Details' : 'Pickup Details'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                <div>
                  <Label htmlFor="delivery-date" className="text-xs font-medium">
                    {method === 'delivery' ? 'Delivery Date' : 'Pickup Date'}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                        id="delivery-date"
                        variant={"outline"}
                            className={cn(
                          "w-full justify-start text-left font-normal h-9 mt-1 text-sm",
                          !deliveryDate && "text-muted-foreground"
                            )}
                          >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {deliveryDate ? format(deliveryDate, "PPP") : <span>Select a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={deliveryDate}
                            onSelect={setDeliveryDate}
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                        classNames={{
                          day_outside: "text-muted-foreground",
                          day_disabled: "text-muted-foreground opacity-50",
                        }}
                            disabled={(date) => {
                          // Always disable past dates. We compare against the start of today.
                          const startOfToday = new Date();
                          startOfToday.setHours(0, 0, 0, 0);
                          if (date < startOfToday) {
                              return true;
                          }

                          // If florist data is not loaded, disable all dates.
                          if (!floristData) {
                              return true;
                          }

                          // --- PICKUP LOGIC ---
                          if (method === 'pickup') {
                              const dayOfWeek = format(date, "eeee").toLowerCase(); // e.g., 'monday'
                              const formattedDate = format(date, 'yyyy-MM-dd');
                              const isToday = formattedDate === format(new Date(), 'yyyy-MM-dd');
                              
                              // 1. Check blackout dates for pickup
                              if (floristData.blackout_dates?.includes(formattedDate)) {
                                  return true;
                              }

                              // 2. Check business hours
                              const businessHours = floristData.business_hours;
                              if (!businessHours || !businessHours[dayOfWeek] || businessHours[dayOfWeek].closed) {
                                  return true;
                                }
                              
                              // 3. For today, check if store is currently open
                              if (isToday && !isStoreOpen(businessHours)) {
                                  return true; // Disable today if store is currently closed
                              }
                              
                              return false; // If not disabled by above, it's enabled for pickup
                          }

                          // --- DELIVERY LOGIC ---
                          if (method === 'delivery') {
                              const dayOfWeek = format(date, "eeee"); // e.g., 'Monday'
                              const formattedDate = format(date, 'yyyy-MM-dd');
                              const isToday = formattedDate === format(new Date(), 'yyyy-MM-dd');

                              // 1. Check for blackout dates first
                              if (floristData.blackout_dates?.includes(formattedDate)) {
                                  return true;
                              }
                                  
                              // 2. Handle same-day delivery logic for today
                              if (isToday) {
                                  if (!floristData.same_day_enabled) {
                                      return true; // Same day is disabled entirely
                                  }
                                  const cutoffTimes = floristData.delivery_cutoff_times;
                                  const cutoffTime = cutoffTimes ? cutoffTimes[dayOfWeek] : null;
                                  
                                  if (!cutoffTime) { // This day is not a same-day delivery day
                                      return true;
                                  }

                                  if (isPastCutoffTime(cutoffTime)) {
                                      return true; // It's past the cutoff time for today.
                                  }
                              } else { // 3. Handle future (non-same-day) delivery logic
                                  if (!floristData.non_sameday_enabled) {
                                      return true; // Future delivery is disabled entirely.
                                  }
                                  
                                  // Check if the day of the week is allowed for future delivery
                                  let allowedDays = floristData.non_sameday_days || [];

                                  // Defensive check for data that might be stored as a stringified JSON array
                                  if (typeof allowedDays === 'string' && allowedDays.startsWith('[') && allowedDays.endsWith(']')) {
                                    try {
                                      allowedDays = JSON.parse(allowedDays);
                                    } catch (e) {
                                      console.error("Could not parse non_sameday_days:", e);
                                      allowedDays = [];
                                    }
                                  }

                                  if (!Array.isArray(allowedDays) || !allowedDays.includes(dayOfWeek)) {
                                      return true; // This day of the week is not available for future delivery.
                                }
                              }
                              
                              // If we've passed all checks, the date is enabled for delivery
                              return false;
                          }

                          // Default to disabled if no method is matched (should not happen)
                          return true;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                  {validationErrors?.date && <p className="text-xs text-red-500 mt-1">{validationErrors.date}</p>}
                    </div>
                {method === 'delivery' && isLoaded && (
                  <div>
                    <Label htmlFor="delivery-address" className="text-xs font-medium">Delivery Address</Label>
                            <PlaceAutocomplete
                            ref={autocompleteInputRef}
                              onPlaceSelect={handlePlaceSelect}
                      className="w-full h-9 mt-1"
                    />
                    {validationErrors?.address && <p className="text-xs text-red-500 mt-1">{validationErrors.address}</p>}
                    {validationErrors?.distance && <p className="text-xs text-red-500 mt-1">{validationErrors.distance}</p>}
                            </div>
                          )}
                {method === 'pickup' && floristData && (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="font-medium text-sm">{floristData.store_name}</p>
                    <p className="text-xs text-gray-600">{floristData.address}</p>
                        </div>
                )}
              </CardContent>
            </Card>

            {/* Your Information Card */}
            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Your Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-3">
                            <div>
                    <Label htmlFor="buyer-first-name" className="text-xs font-medium">First Name</Label>
                    <Input id="buyer-first-name" value={buyerInfo.firstName} onChange={(e) => setBuyerInfo({ ...buyerInfo, firstName: e.target.value })} className="h-9 mt-1 text-sm" />
                            </div>
                            <div>
                    <Label htmlFor="buyer-last-name" className="text-xs font-medium">Last Name</Label>
                    <Input id="buyer-last-name" value={buyerInfo.lastName} onChange={(e) => setBuyerInfo({ ...buyerInfo, lastName: e.target.value })} className="h-9 mt-1 text-sm" />
                            </div>
                  <div className="col-span-2">
                    <Label htmlFor="buyer-email" className="text-xs font-medium">Email</Label>
                    <Input id="buyer-email" type="email" value={buyerInfo.email} onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })} className="h-9 mt-1 text-sm" />
                    {validationErrors?.email && <p className="text-xs text-red-500 mt-1">{validationErrors.email}</p>}
                          </div>
                  <div className="col-span-2">
                    <Label htmlFor="buyer-phone" className="text-xs font-medium">Phone</Label>
                    <Input id="buyer-phone" type="tel" value={buyerInfo.phone} onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })} className="h-9 mt-1 text-sm" />
                          </div>
                        </div>
              </CardContent>
            </Card>

            {method === 'delivery' && (
              <Card className="shadow-md">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Recipient's Information</CardTitle>
                  <Button variant="link" size="sm" onClick={copyBuyerToRecipient} className="text-xs">Same as mine</Button>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-3">
                            <div>
                      <Label htmlFor="recipient-first-name" className="text-xs font-medium">First Name</Label>
                      <Input id="recipient-first-name" value={recipientInfo.firstName} onChange={(e) => setRecipientInfo({ ...recipientInfo, firstName: e.target.value })} className="h-9 mt-1 text-sm" />
                            </div>
                            <div>
                      <Label htmlFor="recipient-last-name" className="text-xs font-medium">Last Name</Label>
                      <Input id="recipient-last-name" value={recipientInfo.lastName} onChange={(e) => setRecipientInfo({ ...recipientInfo, lastName: e.target.value })} className="h-9 mt-1 text-sm" />
                            </div>
                    <div className="col-span-2">
                      <Label htmlFor="recipient-phone" className="text-xs font-medium">Phone</Label>
                      <Input id="recipient-phone" type="tel" value={recipientInfo.phone} onChange={(e) => setRecipientInfo({ ...recipientInfo, phone: e.target.value })} className="h-9 mt-1 text-sm" />
                          </div>
                          </div>
                </CardContent>
              </Card>
            )}

            {/* SMS Opt-in Section */}
            <SMSOptInSection
              customerPhone={buyerInfo.phone}
              onPhoneChange={(phone) => setBuyerInfo({ ...buyerInfo, phone })}
              smsOptIn={smsOptIn}
              onSmsOptInChange={setSmsOptIn}
              smsUpdatesFee={smsUpdatesFee}
              onSmsUpdatesFeeChange={setSmsUpdatesFee}
            />

            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Special Instructions / Gift Message</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                      <Textarea
                        value={specialInstructions}
                        onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="e.g., Leave at front door behind pot plant"
                  className="h-20 resize-none text-sm"
                />
              </CardContent>
            </Card>
            
            {/* Mobile Proceed Button - Under the form */}
            <div className="lg:hidden">
                      <Button
                onClick={handlePlaceOrder}
                      disabled={isLoading}
                className="w-full bg-[#843329] text-white hover:bg-[#967F81] h-12 text-base font-medium"
                      >
                      {isLoading ? 'Processing...' : 'Proceed to Secure Payment'} 
                      </Button>
                    </div>
            
            <div className="pt-2">
              <Button variant="ghost" onClick={() => navigate('/cart')} size="sm" className="text-xs">&larr; Back to Cart</Button>
          </div>

                      </div>

          {/* Desktop Right Column: Order Summary - Hidden on mobile */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="sticky top-24 space-y-4">
              <Card className="shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {items && items.map(item => (
                    <div key={item.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center">
                        <img src={item.image} alt={item.title} className="w-10 h-10 rounded object-cover mr-2" />
                      <div>
                          <p className="font-medium text-xs">{item.title}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                      <p className="text-xs font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
                  <Separator className="my-3" />
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Subtotal</span>
                      <span>${total?.toFixed(2)}</span>
                  </div>
                    <div className="flex justify-between text-xs">
                      <span>Delivery Fee</span>
                      <span>
                        {method === 'delivery' ? `$${(floristData?.delivery_fee || 0).toFixed(2)}` : 'N/A'}
                      </span>
                    </div>
                    <Separator className="my-2" />
                    {smsOptIn && smsUpdatesFee > 0 && (
                      <div className="flex justify-between text-xs">
                        <span>SMS Updates</span>
                        <span>${smsUpdatesFee.toFixed(2)}</span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold text-sm">
                    <span>Total</span>
                    <span>${((total || 0) + (method === 'delivery' ? (floristData?.delivery_fee || 0) : 0) + smsUpdatesFee).toFixed(2)}</span>
                  </div>
                </div>
                  
                  <Button
                    onClick={handlePlaceOrder}
                    disabled={isLoading}
                    className="w-full bg-[#843329] text-white hover:bg-[#967F81] mt-4 h-10 text-sm"
                  >
                    {isLoading ? 'Processing...' : 'Proceed to Secure Payment'}
                  </Button>
              </CardContent>
            </Card>

              <Card className="shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{method === 'pickup' ? 'Business Hours' : 'Florist Details'}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {floristData && (
                    <div className="space-y-3">
                      {/* Store Info */}
                  <div>
                        <p className="font-medium text-sm">{floristData.store_name}</p>
                        <div className="text-xs text-gray-600 mt-1">
                          {floristData.unit_number && `${floristData.unit_number}/`}
                          {floristData.street_number} {floristData.street_name}
                          <br />
                          {floristData.suburb}, {floristData.state} {floristData.postcode}
                        </div>
                  </div>

                      {method === 'pickup' ? (
                        /* Business Hours for Pickup */
                        <div>
                          <p className="font-medium mb-2 text-xs">Store Hours</p>
                          <div className="text-xs space-y-1 bg-blue-50 p-2 rounded">
                            {floristData.business_hours && typeof floristData.business_hours === 'object' ? (
                              Object.entries(floristData.business_hours).map(([day, hours]: [string, any]) => (
                                <div key={day} className="flex justify-between items-center">
                                  <span className="text-gray-700 capitalize">{day}</span>
                                  <span className="text-gray-600 font-medium">
                                    {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-600">Business hours not available</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* Delivery Information */
                        <>
                          {/* Same Day Delivery */}
                          {floristData.same_day_enabled && floristData.delivery_cutoff_times && (
                            <div>
                              <p className="font-medium mb-2 text-xs text-brand-primary">Same Day Delivery</p>
                              <div className="text-xs space-y-1 bg-[#967F81]/10 p-2 rounded">
                                {Object.entries(floristData.delivery_cutoff_times).map(([day, cutoffTime]) => (
                                  <div key={day} className="flex justify-between items-center">
                                    <span className="text-gray-700">{day}</span>
                                    <span className="text-gray-600 font-medium">Before {cutoffTime as string}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                          {/* Non-Same Day Delivery */}
                          {floristData.non_sameday_enabled && floristData.non_sameday_days && (
                            <div>
                              <p className="font-medium mb-2 text-xs text-blue-600">Advance Delivery</p>
                              <div className="text-xs bg-blue-50 p-2 rounded">
                                <p className="text-gray-700">
                                  Available: {Array.isArray(floristData.non_sameday_days) 
                                    ? floristData.non_sameday_days.join(', ')
                                    : 'Multiple days available'
                                  }
                                </p>
                                <p className="text-gray-600 mt-1">Order in advance for these days</p>
                  </div>
                            </div>
                          )}
                          
                          {/* Delivery Info */}
                          <div>
                            <p className="font-medium mb-2 text-xs">Delivery Information</p>
                            <div className="text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-700">Delivery Radius</span>
                                <span className="text-gray-600">{floristData.delivery_radius || 0}km</span>
                    </div>
                              <div className="flex justify-between">
                                <span className="text-gray-700">Delivery Fee</span>
                                <span className="text-gray-600">${(floristData.delivery_fee || 0).toFixed(2)}</span>
                    </div>
                              {floristData.minimum_order_amount && (
                                <div className="flex justify-between">
                                  <span className="text-gray-700">Minimum Order</span>
                                  <span className="text-gray-600">${floristData.minimum_order_amount.toFixed(2)}</span>
                  </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryDetails; 